/**
 * Submit-time helpers used by OrderStart / OrderReadyToSend.
 *
 * After the ordering-domain → microapp consolidation, the SPA reads the test
 * catalog and per-test consent flow directly from ``UiConfig.testTypes``.
 * The HTTP route ``/config/ordering-domain/{org}`` no longer exists.
 */

import { getTestTypeConfig, type UiConfig } from '@/config/uiConfig'
import { TestType } from '@/types/RequisitionFormTypes'

export type DomainTestLine = { code: string; consentFlowId: string }

/**
 * Build the submit catalog (code → consentFlowId) from the microapp UI config.
 * ``consentFlow`` defaults to ``'DEFAULT'`` so downstream resolution can fall
 * back to the org-level ``UiConfig.consentFlow``.
 */
export function testLinesFromUiConfig(config: Pick<UiConfig, 'testTypes'> | null | undefined): DomainTestLine[] {
  const types = Array.isArray(config?.testTypes) ? config!.testTypes : []
  return types.flatMap((t) => {
    const code = typeof t.code === 'string' ? t.code.trim() : ''
    if (!code) return []
    const consentFlowId = typeof t.consentFlow === 'string' && t.consentFlow.length > 0 ? t.consentFlow : 'DEFAULT'
    return [{ code, consentFlowId }]
  })
}

export function consentFlowIdForSubmit(selectedTestCode: string | undefined | null, catalog: DomainTestLine[]): string {
  const code = selectedTestCode?.trim() ?? ''
  if (!code) return 'DEFAULT'
  const row = catalog.find((t) => t.code === code)
  return row?.consentFlowId?.trim() || 'DEFAULT'
}

/**
 * Resolves the effective ordering consent flow (UPPER_CASE) for the selected test,
 * matching OrderReadyToSend: per-test catalog row, org default, per-test UI config, then catalog fallback.
 */
export function resolveEffectiveConsentFlowForSubmit(
  config: Pick<UiConfig, 'consentFlow' | 'testTypes'>,
  selectedTestCode: string | undefined | null,
  catalog: DomainTestLine[]
): string {
  const testCode = selectedTestCode?.trim() ?? ''
  const catalogRow = catalog.find((t) => t.code === testCode)
  const lineConsent = catalogRow?.consentFlowId?.trim()
  const orderingDomainDefaultConsentFlow = (config.consentFlow ?? '').trim() || null
  const uiTestConsentFlow = getTestTypeConfig(testCode as TestType, config as UiConfig)?.consentFlow?.trim()

  return lineConsent && lineConsent !== 'DEFAULT'
    ? lineConsent
    : (orderingDomainDefaultConsentFlow || '').trim() ||
        (uiTestConsentFlow && uiTestConsentFlow !== 'DEFAULT' ? uiTestConsentFlow : '') ||
        (config.consentFlow || '').trim() ||
        consentFlowIdForSubmit(testCode, catalog)
}
